import { useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setAppError } from "../../../../../store/userSlice";
import SmartDownloadPagination from "../../../../../components/SmartDataTable/SmartDownloadPagination";
import TerminationPopUp from "../TerminationFinalExit/TerminationPopUp.js/TerminationPopUp";
import { GoEye } from "react-icons/go";
import { checkAccessFn } from "../../../../../utils/checkAccess";
import {
  NAKAL_KAFALA_COLUMNS,
  NAKAL_KAFALA_CONFIG,
  NAKAL_KAFALA_SEARCHABLE_FIELDS,
  renderVisaProvider,
  renderNakalKafalaStatus
} from './NakalKafalaConstants';

const NakalKafalaList = ({
  setTrigger,
  trigger,
}) => {
  const dispatch = useDispatch();
  const dashboardData = useSelector((state) => state.dashboardData);
  const checkAccess = (module, name) => checkAccessFn(dashboardData, module, name);
  
  // Get token from localStorage
  const token = useMemo(() => localStorage.getItem("nodeToken"), []);

  // Popup state management
  const [popupState, setPopupState] = useState(() => ({
    nakalKafala: false,
    propData: null
  }));

  // Handle eye icon click to open popup
  const handleViewClick = useCallback((item) => {
    setPopupState({
      nakalKafala: true,
      propData: item
    });
  }, []);

  // Close popup handler
  const handleClosePopup = useCallback(() => {
    setPopupState({
      nakalKafala: false,
      propData: null
    });
  }, []);

  // Custom cell renderer for special cases
  const renderCustomCell = useCallback((item, column, rowIndex, currentPage, pageSize) => {
    switch (column.key) {
      case 'serial':
        return (currentPage - 1) * pageSize + rowIndex + 1;
      
      case 'visa_provider':
        return renderVisaProvider(item);
      
      case 'total_salary':
        return item?.total_salary?.toString() || "-";
      
      case 'idle_aging':
        return item?.idle_aging?.toString() || "-";
      
      case 'nakal_kafala_status':
        return renderNakalKafalaStatus(item);
      
      case 'action':
        return checkAccess("DEMOBILIZATION", "nakal-kafala-demobilization-edit") ? (
          <div
            className="ml-2 cursor-pointer"
            onClick={() => handleViewClick(item)}
          >
            <GoEye fontSize={22} className="ml-2" />
          </div>
        ) : null;
      
      default:
        return null;
    }
  }, [handleViewClick, dashboardData]);

  // Download handlers
  const handleDownloadComplete = useCallback((result) => {
    dispatch(setAppError({
      msg: `Excel Downloaded (${result.recordCount} records)`,
      color: "success",
    }));
  }, [dispatch]);

  const handleDownloadError = useCallback((error) => {
    dispatch(setAppError({
      msg: error.message || "Download failed",
      color: "error"
    }));
  }, [dispatch]);

  return (
    <div className="p-2">
      <SmartDownloadPagination
        // API Configuration
        apiEndpoint={NAKAL_KAFALA_CONFIG.API_ENDPOINT}
        downloadEndpoint={NAKAL_KAFALA_CONFIG.DOWNLOAD_ENDPOINT}
        token={token}
        
        // Table Configuration
        columns={NAKAL_KAFALA_COLUMNS}
        searchableFields={NAKAL_KAFALA_SEARCHABLE_FIELDS}
        enableHighlighting={true}
        
        // Pagination Configuration
        defaultPageSize={NAKAL_KAFALA_CONFIG.DEFAULT_PAGE_SIZE}
        pageSizeOptions={NAKAL_KAFALA_CONFIG.PAGE_SIZE_OPTIONS}
        
        // Download Configuration
        downloadFilename={NAKAL_KAFALA_CONFIG.DOWNLOAD_FILENAME}
        enableDownload={true}
        
        // Search Configuration
        enableSearch={true}
        searchPlaceholder={NAKAL_KAFALA_CONFIG.SEARCH_PLACEHOLDER}
        searchDebounceMs={NAKAL_KAFALA_CONFIG.DEBOUNCE_MS}
        
        // Trigger for cache invalidation
        trigger={trigger}
        
        // Customization
        renderCustomCell={renderCustomCell}
        
        // Event Handlers
        onDownloadComplete={handleDownloadComplete}
        onDownloadError={handleDownloadError}
        
        // Additional Props
        noDataMessage={NAKAL_KAFALA_CONFIG.NO_DATA_MESSAGE}
        className="nakal-kafala-table"
      />

      {/* Popup rendering */}
      {popupState.nakalKafala && (
        <TerminationPopUp
          PopUp={popupState.nakalKafala}
          setPopUp={handleClosePopup}
          setTrigger={setTrigger}
          trigger={trigger}
          propData={popupState.propData}
          endpoint="NakalKafala"
        />
      )}
    </div>
  );
};

NakalKafalaList.displayName = 'NakalKafalaList';

export default NakalKafalaList;
